import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  ShieldCheck, RefreshCw, AlertTriangle, CheckCircle, Clock,
  Building2, TrendingUp, FileText, XCircle,
  Activity, Truck, Shield, Users, UserCheck, Search,
  Database, Link2, LinkIcon, Zap, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const SEVERITY_MAP = {
  critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: XCircle, label: 'Expiring Soon', dot: 'bg-red-500' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: AlertTriangle, label: 'Renew Soon', dot: 'bg-amber-500' },
  ok: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: CheckCircle, label: 'Active', dot: 'bg-green-500' },
};

const HISTORY_ICON = {
  pass: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50' },
  fail: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50' },
  info: { icon: Activity, color: 'text-blue-500', bg: 'bg-blue-50' },
};

// --- Sub-components ---

const ScoreGauge = ({ score, label }) => {
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#eab308' : '#ef4444';
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-32">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="54" fill="none" stroke="#e2e8f0" strokeWidth="8" />
          <circle cx="60" cy="60" r="54" fill="none" stroke={color} strokeWidth="8"
            strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
            className="transition-all duration-1000 ease-out" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-slate-900">{score}</span>
          <span className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">/ 100</span>
        </div>
      </div>
      <p className="text-xs font-medium text-slate-600 mt-2">{label}</p>
    </div>
  );
};

const SourceBadge = ({ source }) => {
  if (source === 'live') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">
        <Zap className="h-2.5 w-2.5" /> LIVE
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">
      <Database className="h-2.5 w-2.5" /> MOCK
    </span>
  );
};

const ProviderPanel = ({ name, data, icon: Icon, accentColor, onRun, running, isConfigured }) => {
  const hasData = data && data.status === 'active';
  const source = data?.source || 'mock';

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`panel-${name.toLowerCase()}`}>
      <div className={`h-1.5 bg-gradient-to-r ${accentColor}`} />
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className={`p-2 rounded-lg bg-gradient-to-br ${accentColor} bg-opacity-10`}>
              <Icon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">{name}</h3>
              <p className="text-[10px] text-slate-400">{hasData ? `Last checked: ${new Date(data.checked_at).toLocaleDateString()}` : 'Not yet checked'}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {hasData && <SourceBadge source={source} />}
            {hasData && (
              <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" /> Active
              </span>
            )}
          </div>
        </div>

        {hasData && data.details ? (
          <div className="grid grid-cols-2 gap-2 mb-4">
            {Object.entries(data.details).filter(([key]) => !['basics', '__v'].includes(key)).map(([key, value]) => (
              <div key={key} className="bg-slate-50 rounded-lg p-2.5">
                <p className="text-[10px] text-slate-500 capitalize">{key.replace(/_/g, ' ')}</p>
                <p className={`text-xs font-semibold ${typeof value === 'boolean' ? (value ? 'text-green-700' : 'text-red-700') : 'text-slate-900'}`}>
                  {typeof value === 'boolean' ? (value ? 'Verified' : 'Not Verified') : String(value)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-slate-50 rounded-lg p-6 mb-4 text-center">
            <Shield className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-xs text-slate-500">
              {isConfigured === false ? `${name} API not configured — using mock data` : `Run a check to see ${name} compliance details`}
            </p>
          </div>
        )}

        <button onClick={onRun} disabled={running}
          className={`w-full py-2.5 text-xs font-semibold rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${hasData ? 'border border-slate-200 text-slate-700 hover:bg-slate-50' : 'bg-gradient-to-r ' + accentColor + ' text-white hover:shadow-lg'}`}
          data-testid={`run-${name.toLowerCase()}-check-btn`}>
          {running ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
          {running ? 'Checking...' : hasData ? 'Re-Run Check' : `Run ${name} Check`}
        </button>
      </div>
    </motion.div>
  );
};

const FmcsaPanel = ({ data, onRun, running }) => {
  const hasData = data && (data.status === 'active' || data.status === 'issue');
  const details = data?.details || {};
  const basics = details.basics || [];
  const [dotInput, setDotInput] = React.useState(data?.dot_number || '');

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="panel-fmcsa">
      <div className="h-1.5 bg-gradient-to-r from-sky-500 to-cyan-500" />
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-gradient-to-br from-sky-500 to-cyan-500">
              <Database className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">FMCSA</h3>
              <p className="text-[10px] text-slate-400">
                {hasData ? `Last checked: ${new Date(data.checked_at).toLocaleDateString()}` : 'Federal Motor Carrier Safety Administration'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {hasData && <SourceBadge source="live" />}
            {hasData && (
              <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold flex items-center gap-1 ${data.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {data.status === 'active' ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                {data.status === 'active' ? 'Authorized' : 'Issue'}
              </span>
            )}
          </div>
        </div>

        {/* DOT Number Input */}
        <div className="mb-4">
          <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">DOT Number</label>
          <input type="text" value={dotInput} onChange={(e) => setDotInput(e.target.value)}
            placeholder="Enter DOT number (e.g. 860030)"
            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-sky-500"
            data-testid="fmcsa-dot-input" />
        </div>

        {hasData ? (
          <>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {[
                ['Legal Name', details.legal_name],
                ['DOT Number', details.dot_number],
                ['Operating Status', details.operating_status],
                ['Safety Rating', details.safety_rating || 'Not Rated'],
                ['Power Units', details.power_units],
                ['Drivers', details.drivers],
                ['Crash Total', details.crash_total],
                ['Address', details.physical_address],
              ].filter(([, v]) => v !== undefined && v !== '' && v !== 0).map(([label, value]) => (
                <div key={label} className="bg-slate-50 rounded-lg p-2.5">
                  <p className="text-[10px] text-slate-500">{label}</p>
                  <p className="text-xs font-semibold text-slate-900">{String(value)}</p>
                </div>
              ))}
            </div>

            {basics.length > 0 && (
              <div className="mb-4">
                <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-2">Safety BASICs</p>
                <div className="space-y-1.5">
                  {basics.map((b) => (
                    <div key={b.name} className="flex items-center gap-2 text-xs">
                      <span className="w-32 text-slate-600 truncate">{b.name}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${b.percentile > 75 ? 'bg-red-500' : b.percentile > 50 ? 'bg-amber-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min(100, b.percentile || 0)}%` }}
                        />
                      </div>
                      <span className={`w-10 text-right font-semibold ${b.percentile > 75 ? 'text-red-600' : b.percentile > 50 ? 'text-amber-600' : 'text-green-600'}`}>
                        {b.percentile != null ? `${b.percentile}%` : 'N/A'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-slate-50 rounded-lg p-4 mb-4 text-center">
            <Database className="h-6 w-6 text-slate-300 mx-auto mb-1.5" />
            <p className="text-xs text-slate-500">Enter your DOT number above and click lookup</p>
          </div>
        )}

        <button onClick={() => onRun(dotInput)} disabled={running || !dotInput.trim()}
          className={`w-full py-2.5 text-xs font-semibold rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${hasData ? 'border border-slate-200 text-slate-700 hover:bg-slate-50' : 'bg-gradient-to-r from-sky-500 to-cyan-500 text-white hover:shadow-lg'}`}
          data-testid="run-fmcsa-check-btn">
          {running ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Database className="h-3.5 w-3.5" />}
          {running ? 'Looking up DOT...' : hasData ? 'Re-Run FMCSA Lookup' : 'Run FMCSA Lookup'}
        </button>
      </div>
    </motion.div>
  );
};

const IntegrationStatusBar = ({ integrations }) => {
  const items = [
    { key: 'fmcsa', label: 'FMCSA', color: 'sky', url: 'https://mobile.fmcsa.dot.gov/QCDevsite/docs/getStarted' },
    { key: 'rmis', label: 'RMIS', color: 'emerald', url: 'https://truckstop.com' },
    { key: 'highway', label: 'Highway', color: 'blue', url: 'https://highway.com' },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-slate-200 rounded-xl p-4 mb-6" data-testid="integration-status-bar">
      <div className="flex items-center gap-2 mb-3">
        <Link2 className="h-4 w-4 text-slate-500" />
        <h3 className="text-xs font-bold text-slate-900">Integration Status</h3>
      </div>
      <div className="flex flex-wrap gap-3">
        {items.map(({ key, label, color, url }) => {
          const connected = integrations?.[key];
          return (
            <div key={key} className="flex items-center gap-2 text-xs">
              <div className={`w-2 h-2 rounded-full ${connected ? `bg-${color}-500` : 'bg-slate-300'}`} />
              <span className={`font-medium ${connected ? 'text-slate-900' : 'text-slate-400'}`}>{label}</span>
              {connected ? (
                <span className="text-[10px] text-green-600 font-semibold">Connected</span>
              ) : (
                <a href={url} target="_blank" rel="noopener noreferrer"
                  className="text-[10px] text-slate-400 hover:text-blue-500 flex items-center gap-0.5">
                  Set up <ExternalLink className="h-2.5 w-2.5" />
                </a>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

const InsuranceAlert = ({ alert, index }) => {
  const s = SEVERITY_MAP[alert.severity] || SEVERITY_MAP.ok;
  const Icon = s.icon;
  const daysLeft = Math.ceil((new Date(alert.expires) - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }}
      className={`${s.bg} border ${s.border} rounded-lg p-3.5 flex items-center gap-3`} data-testid={`alert-${alert.type}`}>
      <Icon className={`h-5 w-5 ${s.text} flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-slate-900">{alert.label}</p>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.bg} ${s.text}`}>{s.label}</span>
        </div>
        <div className="flex items-center gap-4 mt-0.5">
          <p className="text-xs text-slate-500">Coverage: <span className="font-medium text-slate-700">{alert.coverage}</span></p>
          <p className="text-xs text-slate-500">Expires: <span className={`font-medium ${daysLeft <= 14 ? 'text-red-700' : daysLeft <= 30 ? 'text-amber-700' : 'text-green-700'}`}>{alert.expires} ({daysLeft}d)</span></p>
        </div>
      </div>
    </motion.div>
  );
};

const TimelineEvent = ({ event, index, isLast }) => {
  const style = HISTORY_ICON[event.status] || HISTORY_ICON.info;
  const Icon = style.icon;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }}
      className="flex gap-3" data-testid={`timeline-event-${index}`}>
      <div className="flex flex-col items-center">
        <div className={`p-1.5 rounded-full ${style.bg}`}><Icon className={`h-4 w-4 ${style.color}`} /></div>
        {!isLast && <div className="w-px flex-1 bg-slate-200 mt-1" />}
      </div>
      <div className="pb-5 flex-1">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-slate-900">{event.event}</p>
          <span className="text-[10px] text-slate-400 uppercase font-medium">{event.provider}</span>
        </div>
        <p className="text-xs text-slate-500 mt-0.5">{event.result}</p>
        <p className="text-[10px] text-slate-400 mt-1">{new Date(event.timestamp).toLocaleString()}</p>
      </div>
    </motion.div>
  );
};

// --- Main Page ---

const ComplianceDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningFmcsa, setRunningFmcsa] = useState(false);
  const [runningRmis, setRunningRmis] = useState(false);
  const [runningHighway, setRunningHighway] = useState(false);
  const [driverSearch, setDriverSearch] = useState('');

  useEffect(() => {
    if (user && user.role !== 'carrier_owner' && !user.is_admin) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const fetchDashboard = useCallback(async () => {
    try {
      const { data: d } = await api.get('/compliance/dashboard');
      setData(d);
    } catch {
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const runCheck = async (provider, dotNumber) => {
    const setters = { fmcsa: setRunningFmcsa, rmis: setRunningRmis, highway: setRunningHighway };
    const setRunning = setters[provider];
    setRunning(true);
    try {
      const payload = provider === 'fmcsa' && dotNumber ? { dot_number: dotNumber } : {};
      const { data: result } = await api.post(`/compliance/${provider}/check`, payload);
      if (result.success) {
        toast.success(`${provider.toUpperCase()} check completed`);
      } else {
        toast.error(result.error || `${provider.toUpperCase()} check failed`);
      }
      await fetchDashboard();
    } catch {
      toast.error(`${provider.toUpperCase()} check failed`);
    } finally {
      setRunning(false);
    }
  };

  const alerts = useMemo(() => data?.insurance_alerts || [], [data]);
  const drivers = useMemo(() => data?.drivers || [], [data]);
  const criticalAlerts = useMemo(() => alerts.filter(a => a.severity === 'critical').length, [alerts]);
  const filteredDrivers = useMemo(() => drivers.filter(d =>
    d.name?.toLowerCase().includes(driverSearch.toLowerCase()) ||
    d.email?.toLowerCase().includes(driverSearch.toLowerCase()) ||
    d.location?.toLowerCase().includes(driverSearch.toLowerCase())
  ), [drivers, driverSearch]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-green-500 border-r-transparent" />
          <p className="mt-3 text-slate-500 text-sm">Loading compliance data...</p>
        </div>
      </div>
    );
  }

  const fmcsaData = data?.fmcsa;
  const rmisData = data?.rmis;
  const highwayData = data?.highway;
  const history = data?.history || [];
  const overall = data?.overall_score || 0;
  const driverStats = data?.driver_stats || {};
  const integrations = data?.integrations || {};

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" data-testid="compliance-dashboard">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -15 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2.5" data-testid="compliance-title">
              <ShieldCheck className="h-7 w-7 text-green-600" /> Carrier Compliance
            </h1>
            <p className="text-slate-500 text-sm mt-0.5">
              {data?.mc_number && <span className="mr-3">MC: {data.mc_number}</span>}
              {data?.dot_number && <span>DOT: {data.dot_number}</span>}
              {!data?.mc_number && !data?.dot_number && 'Monitor Drivers compliance status'}
            </p>
          </div>
          <button onClick={fetchDashboard} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg hover:bg-white transition-colors" data-testid="refresh-compliance-btn">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </motion.div>

        {/* Integration Status */}
        <IntegrationStatusBar integrations={integrations} />

        {/* Score Overview Row */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-5" data-testid="overall-score-card">
            <ScoreGauge score={overall} label="Overall Score" />
            <div className="flex-1">
              <h3 className="text-sm font-bold text-slate-900 mb-1">Compliance Rating</h3>
              <p className={`text-xs font-semibold ${overall >= 80 ? 'text-green-600' : overall >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                {overall >= 80 ? 'Excellent' : overall >= 60 ? 'Good' : overall > 0 ? 'Needs Attention' : 'Not Checked'}
              </p>
              <p className="text-[10px] text-slate-400 mt-1">
                Based on {[integrations.fmcsa && 'FMCSA', 'RMIS', 'Highway'].filter(Boolean).join(', ')}
              </p>
            </div>
          </motion.div>

          {[
            { label: 'Drivers', value: `${driverStats.verified || 0}/${driverStats.total || 0}`, sub: `${driverStats.verified || 0} verified, ${driverStats.pending || 0} pending`, icon: Users, color: driverStats.total > 0 ? 'text-blue-600' : 'text-slate-400' },
            { label: 'RMIS Status', value: rmisData ? rmisData.details?.safety_rating || 'Active' : 'Not Checked', sub: rmisData ? `OOS Rate: ${rmisData.details?.out_of_service_rate}` : 'Run check to view', icon: Building2, color: rmisData ? 'text-green-600' : 'text-slate-400' },
            { label: 'Highway Score', value: highwayData ? `${highwayData.details?.carrier_score}/100` : 'Not Checked', sub: highwayData ? `Safety: ${highwayData.details?.safety_score} | Risk: ${highwayData.details?.fraud_risk}` : 'Run check to view', icon: TrendingUp, color: highwayData ? 'text-blue-600' : 'text-slate-400' },
          ].map((card, i) => (
            <motion.div key={card.label} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * (i + 1) }}
              className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`summary-${card.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center gap-2 mb-2">
                <card.icon className={`h-4 w-4 ${card.color}`} />
                <p className="text-xs text-slate-500 font-medium">{card.label}</p>
              </div>
              <p className={`text-lg font-bold ${card.color}`}>{card.value}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">{card.sub}</p>
            </motion.div>
          ))}
        </div>

        {/* Provider Panels - 3 columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
          <FmcsaPanel data={fmcsaData} onRun={(dot) => runCheck('fmcsa', dot)} running={runningFmcsa} />
          <ProviderPanel name="RMIS" data={rmisData} icon={Building2} accentColor="from-emerald-500 to-teal-500"
            onRun={() => runCheck('rmis')} running={runningRmis} isConfigured={integrations.rmis} />
          <ProviderPanel name="Highway" data={highwayData} icon={Truck} accentColor="from-blue-500 to-indigo-500"
            onRun={() => runCheck('highway')} running={runningHighway} isConfigured={integrations.highway} />
        </div>

        {/* Driver Compliance */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
          className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="driver-compliance-section">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-500" /> Driver Compliance Overview
            </h2>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-[10px]">
                <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">{driverStats.verified || 0} Verified</span>
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">{driverStats.pending || 0} Pending</span>
                <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">{driverStats.docs_uploaded || 0} Docs</span>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input type="text" placeholder="Search drivers..." value={driverSearch}
                  onChange={(e) => setDriverSearch(e.target.value)}
                  className="pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 w-44"
                  data-testid="driver-search-input" />
              </div>
            </div>
          </div>

          {filteredDrivers.length === 0 ? (
            <div className="text-center py-6">
              <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-xs text-slate-500">{driverSearch ? 'No drivers match your search' : 'No drivers registered yet'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="driver-compliance-table">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-slate-500">
                    <th className="pb-2 pr-3 font-medium">Driver</th>
                    <th className="pb-2 pr-3 font-medium">Verification</th>
                    <th className="pb-2 pr-3 font-medium">Documents</th>
                    <th className="pb-2 pr-3 font-medium">Tier</th>
                    <th className="pb-2 font-medium">Location</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDrivers.map((driver) => (
                    <tr key={driver.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`driver-row-${driver.id}`}>
                      <td className="py-2.5 pr-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-[10px] font-bold text-blue-700 flex-shrink-0">
                            {driver.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{driver.name}</p>
                            <p className="text-[10px] text-slate-400">{driver.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 pr-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${driver.verification_status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {driver.verification_status === 'approved' ? <UserCheck className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                          {driver.verification_status === 'approved' ? 'Verified' : 'Pending'}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3">
                        <div className="flex items-center gap-1.5">
                          <div className="w-14 bg-slate-100 rounded-full h-1.5">
                            <div className={`h-1.5 rounded-full ${driver.verified_badges > 0 ? 'bg-green-500' : 'bg-slate-300'}`}
                              style={{ width: `${driver.total_badges > 0 ? (driver.verified_badges / driver.total_badges) * 100 : 0}%` }} />
                          </div>
                          <span className="text-slate-600">{driver.verified_badges}/{driver.total_badges}</span>
                        </div>
                      </td>
                      <td className="py-2.5 pr-3">
                        <span className="capitalize text-slate-700">{driver.membership_tier || 'Free'}</span>
                      </td>
                      <td className="py-2.5 text-slate-500">{driver.location || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* Insurance Alerts */}
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="lg:col-span-3 bg-white border border-slate-200 rounded-xl p-5" data-testid="insurance-alerts-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <FileText className="h-4 w-4 text-amber-500" /> Insurance & Expiry Alerts
              </h2>
              {criticalAlerts > 0 && (
                <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-red-100 text-red-700 animate-pulse">
                  {criticalAlerts} CRITICAL
                </span>
              )}
            </div>
            <div className="space-y-2.5">
              {alerts.map((alert, i) => <InsuranceAlert key={alert.type} alert={alert} index={i} />)}
            </div>
          </motion.div>

          {/* Compliance History Timeline */}
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5" data-testid="compliance-history-section">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-4">
              <Activity className="h-4 w-4 text-blue-500" /> Compliance History
            </h2>
            {history.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-xs text-slate-500">Run your first compliance check to start building history</p>
              </div>
            ) : (
              <div className="max-h-[380px] overflow-y-auto pr-1">
                {history.map((event, i) => (
                  <TimelineEvent key={event.id} event={event} index={i} isLast={i === history.length - 1} />
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ComplianceDashboard;
